import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  clearWorkbenchDraft,
  readWorkbenchDraft,
  type RestoredWorkbenchDraft,
  type WorkbenchDraftFiles,
  writeWorkbenchDraft,
} from '@/lib/workbench-draft';

interface UseWorkbenchDraftOptions<T> {
  files: WorkbenchDraftFiles;
  key: string;
  onRestore: (draft: RestoredWorkbenchDraft<T>) => void;
  snapshot: T;
  version: number;
}

interface WorkbenchDraftController {
  clear: () => Promise<void>;
  flush: () => Promise<void>;
  isRestored: boolean;
}

export function useWorkbenchDraft<T>({
  files,
  key,
  onRestore,
  snapshot,
  version,
}: UseWorkbenchDraftOptions<T>): WorkbenchDraftController {
  const filesRef = useRef(files);
  const onRestoreRef = useRef(onRestore);
  const restoredRef = useRef(false);
  const restorePromiseRef = useRef<Promise<void>>(Promise.resolve());
  const snapshotRef = useRef(snapshot);
  const [isRestored, setIsRestored] = useState(false);

  filesRef.current = files;
  onRestoreRef.current = onRestore;
  snapshotRef.current = snapshot;

  useEffect(() => {
    let cancelled = false;
    restoredRef.current = false;
    setIsRestored(false);

    const restorePromise = readWorkbenchDraft<T>({ key, version }).then(
      async (draft) => {
        if (cancelled) return;
        if (draft) onRestoreRef.current(draft);
        restoredRef.current = true;
        setIsRestored(true);

        // Let state updates triggered by onRestore render before an immediate
        // login or navigation flush reads the latest refs.
        await new Promise<void>((resolve) => setTimeout(resolve, 0));
      }
    );
    restorePromiseRef.current = restorePromise;

    return () => {
      cancelled = true;
    };
  }, [key, version]);

  const flush = useCallback(async () => {
    await restorePromiseRef.current;
    await writeWorkbenchDraft({
      files: filesRef.current,
      key,
      payload: snapshotRef.current,
      version,
    });
  }, [key, version]);

  const clear = useCallback(async () => {
    await restorePromiseRef.current;
    await clearWorkbenchDraft(key);
  }, [key]);

  useEffect(() => {
    if (!restoredRef.current) return;
    void flush();
  }, [files, flush, snapshot]);

  return useMemo(
    () => ({ clear, flush, isRestored }),
    [clear, flush, isRestored]
  );
}
